import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import '../css/ProjectPage.css';
import '../css/Buttons.css';

import IndexListItem from '../components/IndexListItem';
import SectionCover from '../components/SectionCover';
import TextSlide from '../components/TextSlide';
import VideoSlide from '../components/VideoSlide';
import ColumnTextSlide from '../components/ColumnTextSlide';

import FloatingNavigation from '../components/FloatingNavigation';

import ImageSlide from '../components/ImageSlide';

import wonderkindAccessibility from '../assets/wonderkind-accessibility.webp';
import wonderkindBeforeAfter from '../assets/wonderkind-before-after.webp';
import wonderkindColourSystem from '../assets/wonderkind-colour-system.webp';
import wonderkindCover from '../assets/wonderkind-cover.webp';
import wonderkindDesignPrinciples from '../assets/wonderkind-design-principles.webp';
import wonderkindDesignSystem from '../assets/wonderkind-design-system.webp';
import wonderkindJobLibrary from '../assets/wonderkind-job-library.webp';
import wonderkindNavigationBars from '../assets/wonderkind-navigation-bars.webp';
import wonderkindNewIa from '../assets/wonderkind-new-ia.webp';
import wonderkindOboardingFlo from '../assets/wonderkind-oboarding-flo.webp';
import wonderkindOldIa from '../assets/wonderkind-old-ia.webp';
import wonderkindOldProduct from '../assets/wonderkind-old-product.webp';
import wonderkindOnboarding from '../assets/wonderkind-onboarding.webp';
import wonderkindProjectCover from '../assets/wonderkind-project-cover.webp';
import wonderkindPrototype from '../assets/wonderkind-prototype.webp';
import wonderkindTestResult from '../assets/wonderkind-test-result.webp';
import wonderkindTest from '../assets/wonderkind-test.webp';
import wonderkindUserHierarchy from '../assets/wonderkind-user-hierarchy.webp';






const ProjectWonderkind = () => {

  

  const sections = useMemo(() => [
    { id: 'project-background', label: 'Project background' },
    { id: 'design-system', label: 'Design System' },
    { id: 'navigation', label: 'Navigation' },
  ], []);

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [showFloatingButtons, setShowFloatingButtons] = useState(false);

  // Smooth scroll to a section
  const scrollToElement = (selector) => {
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.warn(`Element not found for selector: ${selector}`);
    }
  };

  // Handle scroll and update visibility/index
  const sectionsRef = useRef(sections);
  const currentIndexRef = useRef(0);

  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  useEffect(() => {
    currentIndexRef.current = currentSectionIndex;
  }, [currentSectionIndex]);

  const handleScroll = useCallback(() => {
    const firstSection = document.getElementById('project-background');
    const firstSectionTop = firstSection ? firstSection.getBoundingClientRect().top : 0;
    setShowFloatingButtons(firstSectionTop <= 0);

    const offsets = sectionsRef.current.map((section) => {
      const el = document.getElementById(section.id);
      return el ? el.getBoundingClientRect().top : Infinity;
    });

    const closestIndex = offsets.findIndex((offset) => offset >= -10);
    if (closestIndex !== -1 && closestIndex !== currentIndexRef.current) {
      setCurrentSectionIndex(closestIndex);
    }
  }, []);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div className="project-body">
      {/* Header */}
      <div className="project-header">
        
        <h2>Wonderkind</h2>
        <div className="project-header-image-wonderkind"></div>
        
      </div>

      {/* Index Section */}
      <section className="project-index">
        <div className="index-wrapper">
          {sections.map((section, index) => (
            <IndexListItem
              key={section.id}
              itemNumber={index + 1}
              label={section.label}
              onClick={() => {
                // When index item is clicked, scroll to the corresponding section
                scrollToElement(`#${section.id}`);
              }}
            />
          ))}
        </div>
      </section>

      {/* Sections */}

      {/* Section 01 */}
      <div className="" id="project-background">
        
        <SectionCover sectionNumber="1" sectionTitle="Project background" />
        
        <ImageSlide 
          imageSlideImage={wonderkindOldProduct}
          imageSlideTitle="Wonderkind was launching a new product to completely replace their old mass job listing and advertising tool"
          
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        
        />


        <TextSlide
          slideTitle="Goals"
          slideText={[
            'Prepare for fast delivery of features',
            'Fix usability issues before launching MVP',
          ]}
        />

      </div>

      
      {/* Section 02 */}

      <div className='' id="design-system">

        <SectionCover sectionNumber="2" sectionTitle="Design System" />

        <TextSlide
          slideTitle="Approach"
          slideText={[
            'Using vuetify as a base design system',
            'Style components',
            'Create new components as needed',
          ]}
        />

        <ImageSlide 
          imageSlideImage={wonderkindDesignSystem}
          imageSlideTitle="Over 30 styled interactive components with Figma and Storybook paired"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindColourSystem}
          imageSlideTitle="I reorganised the existing colours and type styles into systems that could be scaled with the product..."
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindAccessibility}
          imageSlideTitle="...and fixed accessibility issues like improving font sizes and colour contrasts."
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindDesignPrinciples}
          imageSlideTitle="I defined a set of 5 design principles that would serve as a guide to designing components and features."
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        
        
      </div>

      {/* Section 03 */}

      <div className='' id="navigation">

        <SectionCover sectionNumber="3" sectionTitle="Navigation" />

        <TextSlide
          slideTitle="Problem identification"
          slideText={[
            'When launching our new tool, our sales team indicated that all 20 of our pilot test users were having trouble understanding how to reach the “Create campaign” page, the primary use of the tool.',
          ]}
        />

        <ImageSlide 
          imageSlideImage={wonderkindUserHierarchy}
          imageSlideTitle="We had 4 possible types of users defined:"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindOldIa}
          imageSlideTitle="I mapped the old information architecture and the steps of all possible users to reach the job library"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <TextSlide
          slideTitle="Issues identified"
          slideText={[
            'Most users would have a low-level permission, but ALL users had to click through all 4 levels to create a campaign 🤯',
            'Onboarding was done manually for all clients',
            'Several empty states between steps that looked the same and were not actionable',
            'Breadcrumbs were not working',
          ]}
        />

         <ImageSlide 
          imageSlideImage={wonderkindNewIa}
          imageSlideTitle="The first step was to define user permissions"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindOnboarding}
          imageSlideTitle="Then create an onboarding flow and use Auth0 for access management"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindPrototype}
          imageSlideTitle="It was time to go to the playground to prototype possible solutions to bring to management and technical teams."
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindNavigationBars}
          imageSlideTitle="Out of the explored options, the one that worked best for this type of IA was a combo of collapsible local and global navigation bars"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindTest}
          imageSlideTitle="Once we were happy with the prototype, I designed a usability test on Maze to validate if the changes would have the desired outcome"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindTestResult}
          imageSlideTitle="With 17 total participants, the results were very positive"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindBeforeAfter}
          imageSlideTitle="The app was full of dead-ends, over 30 new pages had to be created and every page contained at least two possible actions"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={wonderkindJobLibrary}
          imageSlideTitle="After implementation the tool got a fresh look and feel."
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <TextSlide
                  slideTitle="Results"
                  slideText={[
                    'Faster delivery of features with a solid design system',
                    'Reduction of 90% of support tickets related to navigation and IA',
                    'Tool ready for launch',
                  ]}
                  style={{borderColor: 'green'}}
        />



        
        
      </div>



      

      
      
      <div className="project-header">
        <h2>Thanks!</h2>
        <div className="project-header-image-wonderkind"></div>
      </div>
      



      {/* Floating Navigation */}
      {showFloatingButtons && (
        <FloatingNavigation
          currentSectionIndex={currentSectionIndex}
          totalSections={sections.length}
          onPrevious={() => {
            if (currentSectionIndex > 0) {
              scrollToElement(`#${sections[currentSectionIndex - 1]?.id}`);
            }
          }}
          onNext={() => {
            if (currentSectionIndex < sections.length - 1) {
              scrollToElement(`#${sections[currentSectionIndex + 1]?.id}`);
            }
          }}
          onHome={() => scrollToElement('.project-index')}
        />
      )}
    </div>
  );
};

export default ProjectWonderkind;
